import React, { useContext } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { Button } from '@zillow/constellation';

import AddPropertyConstants from 'app/shared/constants/AddPropertyConstants';

import postingUtils from 'app/shared/utils/postingUtils';
import analyticsUtils from 'app/client/utils/analyticsUtils';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import ListingActivationTerm from 'app/shared/components/ListingActivationTerms';
import { DELIMITER } from 'app/shared/constants/AnalyticsDelimiterConstants';
import PostingActions from 'app/shared/actions/PostingActions';
import stringHelper from 'app/shared/utils/stringHelper';
import PropertyConstants from 'app/shared/constants/PropertyConstants';
import { RMWContext } from 'app/shared/providers/RMWContext';

const { startCaseRemoveSpace } = stringHelper;

const {
    PROPERTY_TYPES: { COMMUNITY },
    LISTING_TYPES: { RENTAL, ROOM },
} = PropertyConstants;

const CTA = {
    TITLE: {
        CREATE_MY_LISTING: 'Create my listing',
        BUILD_A_LEASES: 'Build a lease',
        MANAGE_MY_PROPERTY: 'Manage my property',
        SETUP_APPLICATIONS: 'Set up applications',
        SETUP_PAYMENTS: 'Set up payments',
    },
    GA_ACTION: {
        CREATE_LISTING_APPLICATIONS: `CreateListing${DELIMITER}Applications`,
        CREATE_LISTING_LIST: `CreateListing${DELIMITER}List`,
        CREATE_LISTING_LEASES: `CreateListing${DELIMITER}Leases`,
        CREATE_LISTING_MANAGE: `CreateListing${DELIMITER}Manage`,
        CREATE_LISTING_PAYMENTS: `CreateListing${DELIMITER}Payments`,
    },
};

const getButtonDetails = (mode) => {
    const primary = { type: 'primary' };
    const secondary = { type: 'secondary' };

    switch (mode) {
        case AddPropertyConstants.ADD_MODE.APPLICATIONS:
            primary.title = CTA.TITLE.SETUP_APPLICATIONS;
            primary.gaEvent = CTA.GA_ACTION.CREATE_LISTING_APPLICATIONS;
            primary.mode = AddPropertyConstants.ADD_MODE.APPLICATIONS;

            secondary.title = CTA.TITLE.CREATE_MY_LISTING;
            secondary.gaEvent = CTA.GA_ACTION.CREATE_LISTING_LIST;
            secondary.mode = AddPropertyConstants.ADD_MODE.LIST;
            break;
        case AddPropertyConstants.ADD_MODE.PAYMENTS:
            primary.title = CTA.TITLE.SETUP_PAYMENTS;
            primary.gaEvent = CTA.GA_ACTION.CREATE_LISTING_PAYMENTS;
            primary.mode = AddPropertyConstants.ADD_MODE.PAYMENTS;

            secondary.title = CTA.TITLE.CREATE_MY_LISTING;
            secondary.gaEvent = CTA.GA_ACTION.CREATE_LISTING_LIST;
            secondary.mode = AddPropertyConstants.ADD_MODE.LIST;
            break;
        case AddPropertyConstants.ADD_MODE.LEASES:
            primary.title = CTA.TITLE.BUILD_A_LEASES;
            primary.gaEvent = CTA.GA_ACTION.CREATE_LISTING_LEASES;
            primary.mode = AddPropertyConstants.ADD_MODE.LEASES;

            secondary.title = CTA.TITLE.CREATE_MY_LISTING;
            secondary.gaEvent = CTA.GA_ACTION.CREATE_LISTING_LIST;
            secondary.mode = AddPropertyConstants.ADD_MODE.LIST;
            break;
        default:
            primary.title = CTA.TITLE.CREATE_MY_LISTING;
            primary.gaEvent = CTA.GA_ACTION.CREATE_LISTING_LIST;
            primary.mode = AddPropertyConstants.ADD_MODE.LIST;

            secondary.title = CTA.TITLE.MANAGE_MY_PROPERTY;
            secondary.gaEvent = CTA.GA_ACTION.CREATE_LISTING_MANAGE;
            secondary.mode = AddPropertyConstants.ADD_MODE.DEFAULT;
            break;
    }

    return {
        prefix: `By clicking ${primary.title} or ${secondary.title}`,
        buttonsToRender: [primary, secondary],
    };
};

const propTypes = {
    isSubmitDisabled: PropTypes.bool.isRequired,
    mode: PropTypes.oneOf(Object.values(AddPropertyConstants.ADD_MODE)),
    verifyCanSubmitForm: PropTypes.func.isRequired,
    onStartSubmit: PropTypes.func.isRequired,
    onSubmitSuccess: PropTypes.func.isRequired,
    onNewError: PropTypes.func.isRequired,
    listing: PropTypes.object.isRequired,
};

const defaultProps = {
    mode: AddPropertyConstants.ADD_MODE.DEFAULT,
};

const AddButton = ({
    isSubmitDisabled,
    mode,
    verifyCanSubmitForm,
    onStartSubmit,
    onSubmitSuccess,
    onNewError,
    listing,
}) => {
    const dispatch = useDispatch();
    const { paths } = useContext(RMWContext);
    const { buttonsToRender, prefix } = getButtonDetails(mode);

    const handleAddPropertySubmit = async (gaaction = '', clickedMode) => {
        const canSubmitForm = await verifyCanSubmitForm();

        const {
            isRoomForRent,
            propertyType,
            address,
            unit,
            numUnits,
            customerId,
            duplicateAccountListingId,
        } = listing;
        const listingType = isRoomForRent ? ROOM : RENTAL;
        const isCommunityType = propertyType === COMMUNITY;

        if (canSubmitForm) {
            // Duplicate listing exists. Redirect to existing listing rather than creating new one
            if (duplicateAccountListingId) {
                const gaEvent = {
                    ...AnalyticsConstants.GA_EVENTS.EXISTING_LISTING_REDIRECT,
                };

                dispatch(analyticsUtils.track.GAEventV2(gaEvent));
                const path = postingUtils.getDestinationUrl(paths, clickedMode, duplicateAccountListingId);
                window.router.transitionTo(path);
                onSubmitSuccess(true);
                return;
            }

            try {
                onStartSubmit();
                AnalyticsConstants.GA_EVENTS.ADD_PROPERTY_MODAL_CTA.action = gaaction;
                const { propertyId } = await dispatch(PostingActions.property.create({
                    listingType,
                    propertyType,
                    fullAddress: address,
                    ...(isCommunityType ? { numUnits } : { unitNumber: unit }),
                    customerId,
                }));

                window.dataLayer.push({ event: `RentalManager${DELIMITER}AddProperty` });

                onSubmitSuccess(true);

                dispatch(analyticsUtils.track.GAEventV2({
                    ...AnalyticsConstants.GA_EVENTS.ADD_PROPERTY_MODAL_CTA,
                }));

                window.router.updateLocation(postingUtils.getDestinationUrl(paths, clickedMode, propertyId));
            } catch (err) {
                onNewError('address', err.message);
                const formattedListingType = startCaseRemoveSpace(listingType);
                dispatch(
                    analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.HPPP_NEW_LISTING_ERROR, {
                        action: ['listingCreatedError', formattedListingType, propertyType].join(DELIMITER),
                    }),
                );
            }
        }
    };

    return (
        <div>
            {buttonsToRender.map((button, i) => (
                <Button
                    marginY="xs"
                    fluid={true}
                    buttonType={button.type}
                    onClick={() => handleAddPropertySubmit(button.gaEvent, button.mode)}
                    disabled={isSubmitDisabled}
                    key={`${button.type}-${i}`}
                >
                    {button.title}
                </Button>
            ))}
            <ListingActivationTerm
                actionText={prefix}
                lowlight={true}
            />
        </div>
    );
};

AddButton.propTypes = propTypes;
AddButton.defaultProps = defaultProps;

export default AddButton;
